import type { FunctionComponent, ReactNode } from 'react';
import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../../config/config';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { TitledSection } from '../../../shared/components/titled-section/titled-section';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { PrivacyForm } from '../../components/privacy-form/privacy-form';
import { usePasswordReset } from '../../hooks/use-password-reset';
import {
    useGetUserProfileQuery,
    useOnUserProfileUpdatedSubscription,
} from '../../queries/profile.graphql';

import styles from './settings-page.module.scss';

const style = createStyleHelper(styles, 'settings-page');

export const SettingsPage: FunctionComponent = () => {
    const resetUrl = usePasswordReset();
    const auth = useAuth();
    const userProfileId = auth?.user?.profile.user_profile_id as string;

    const [{ data: userProfiledata, fetching: fetchingUserProfile }, refetch] =
        useGetUserProfileQuery({
            pause: !userProfileId,
            variables: {
                xTenantID: config.TENANT_ID,
                userProfileId: userProfileId,
            },
            requestPolicy: 'cache-and-network',
        });

    const [{ data: profileUpdate }] = useOnUserProfileUpdatedSubscription({
        pause: !userProfileId,
        variables: {
            userProfileId: userProfileId,
        },
    });

    useEffect(() => {
        if (profileUpdate?.userProfileUpdated) {
            refetch();
        }
    }, [profileUpdate?.userProfileUpdated, refetch]);

    const userProfile = userProfiledata?.userProfile;

    return (
        <div className={style()}>
            <TitledSection
                title={
                    <FormattedMessage
                        id="settings-page.change-password.title"
                        defaultMessage="Change Password"
                    />
                }
                description={
                    <FormattedMessage
                        id="settings-page.change-password.information"
                        defaultMessage={
                            'Please <reset-link>click here</reset-link> to update your password.'
                        }
                        values={{
                            ['reset-link']: (text: ReactNode[]) => (
                                <a className={style('action')} href={resetUrl}>
                                    {text}
                                </a>
                            ),
                        }}
                    />
                }
            />

            <TitledSection
                title={
                    <FormattedMessage
                        id="settings-page.privacy-dashboard.title"
                        defaultMessage="Marketing Consent"
                    />
                }
                description={
                    <FormattedMessage
                        id="settings-page.privacy-dashboard.information"
                        defaultMessage={
                            'Your privacy is important to us. You can update your consents any time by approving or disapproving the following marketing channels.'
                        }
                    />
                }
            >
                {userProfile ? (
                    <PrivacyForm
                        userProfile={userProfile}
                        userProfileId={userProfileId}
                        fetchingUserProfile={fetchingUserProfile}
                    />
                ) : null}
            </TitledSection>
        </div>
    );
};
